import {
  PolicyActivity,
  PolicyActivityBase,
  PolicyUpdatedActivity,
  VersionedObjectDiff,
} from '@clearcover/core-app-types';
import React, { FC } from 'react';

import { Asset, CheckmarkIcon, NotTrueIcon, StarIcon } from '@clearcover/atlas';
import { mapWithDefault, usePolicyContext } from 'Policies/utils';
import {
  formatDateTimeShortWithAbbreviatedTimezone,
  snakeToTitleCase,
} from 'Shared/utils';
import { KnownActivityType, PolicyChangeType } from '../../utils';
import styles from './styles.module.css';
import classNames from 'classnames';

const ActivityAssetMap = mapWithDefault({
  map: {
    POLICY_BOUND: StarIcon,
    INCEPTION_OF_THE_POLICY: StarIcon,
    POLICY_CANCELLED: NotTrueIcon,
  },
  defaultValue: CheckmarkIcon,
});
const GenericPolicyActivityIcon: FC<{ activity: PolicyActivityBase }> = ({
  activity,
}) => (
  <Asset
    icon={ActivityAssetMap.get(activity.type)}
    className={`${styles[activity.type]} ${styles.asset}`}
    description={`${snakeToTitleCase(activity.type)} activity icon`}
    height={24}
    width={24}
  />
);

const PolicyActivityTitle: FC<{
  title: string;
  date: string;
  'data-testid': string;
}> = ({ title, date, 'data-testid': dataTestId }) => {
  const timeZone = usePolicyContext().details.timeZone;
  return (
    <div className={styles.title} data-testid={dataTestId}>
      <div className={styles.subType}>
        <span>{title}</span>
        <span className={styles.on}> on </span>
        <span>
          {formatDateTimeShortWithAbbreviatedTimezone(date, timeZone)}
        </span>
      </div>
    </div>
  );
};

const getChangeCount = (activity: PolicyUpdatedActivity) => {
  const {
    insurableItemParties,
    insurableItems,
    policyCoverages,
    policyEndorsements,
    vehicleCoveragesAndEndorsements,
    multiLineDiscounts,
    addresses,
    insurableItemRelationships,
  } = activity.data.changeset;

  return (
    insurableItemParties.length +
    insurableItems.length +
    // Count any policy coverage/endorsement changes as a single change
    (policyCoverages.length + policyEndorsements.length > 0 ? 1 : 0) +
    vehicleCoveragesAndEndorsements.length +
    multiLineDiscounts.length +
    addresses.length +
    insurableItemRelationships.length
  );
};

const PolicyUpdatedActivityIcon: FC<{
  activity: PolicyUpdatedActivity;
}> = ({ activity }) => {
  const changeCount = getChangeCount(activity);
  return <div className={styles.title}>{changeCount}</div>;
};

const policyUpdatedActivityTitle = (activity: PolicyUpdatedActivity) => {
  const {
    insurableItemParties,
    insurableItems,
    policyCoverages,
    policyEndorsements,
    vehicleCoveragesAndEndorsements,
    multiLineDiscounts,
  } = activity.data.changeset;

  const allVehicleCoveragesAndEndorsements = vehicleCoveragesAndEndorsements.flatMap(
    it => [...it.coverages, ...it.endorsements]
  );

  const changeStrings = [
    getChangeString(insurableItemParties, 'Driver', 'ADDED'),
    getChangeString(insurableItemParties, 'Driver', 'DELETED'),
    getChangeString(insurableItemParties, 'Driver', 'UPDATED'),
    getChangeString(insurableItems, 'Vehicle', 'ADDED'),
    getChangeString(insurableItems, 'Vehicle', 'DELETED'),
    getChangeString(insurableItems, 'Vehicle', 'UPDATED'),
    getChangeString(
      [
        ...policyCoverages,
        ...policyEndorsements,
        ...allVehicleCoveragesAndEndorsements,
      ],
      'Coverage',
      'ADDED'
    ),
    getChangeString(
      [
        ...policyCoverages,
        ...policyEndorsements,
        ...allVehicleCoveragesAndEndorsements,
      ],
      'Coverage',
      'DELETED'
    ),
    getChangeString(
      [
        ...policyCoverages,
        ...policyEndorsements,
        ...allVehicleCoveragesAndEndorsements,
      ],
      'Coverage',
      'UPDATED'
    ),
    getChangeString(multiLineDiscounts, 'Discount', 'ADDED'),
    getChangeString(multiLineDiscounts, 'Discount', 'DELETED'),
    getChangeString(multiLineDiscounts, 'Discount', 'UPDATED'),
  ]
    .filter(it => it !== '')
    .map((changeString, i, filteredChangeStrings) => {
      if (filteredChangeStrings.length === 1) {
        return changeString;
      } else if (i === 0 && filteredChangeStrings.length === 2) {
        return changeString + ' ';
      } else if (i + 1 === filteredChangeStrings.length) {
        return 'and ' + changeString;
      }
      return changeString + ', ';
    });

  return changeStrings.length > 0 ? changeStrings.join('') : 'Policy Changes';
};

const getChangeString = (
  diffs: Array<VersionedObjectDiff<any>>,
  entity: string,
  changeType: PolicyChangeType
) => {
  const changeCount = diffs.filter(
    versionable => versionable.type === changeType
  ).length;
  const displayChangeType = mapChangeType(changeType);
  if (changeCount === 0) {
    return '';
  } else if (changeCount === 1) {
    return entity + ' ' + displayChangeType;
  } else if (changeType === 'UPDATED') {
    return entity + ' Changes';
  }
  return entity + 's ' + displayChangeType;
};

const mapChangeType = (changeType: PolicyChangeType) => {
  switch (changeType) {
    case 'ADDED':
      return 'Added';
    case 'DELETED':
      return 'Removed';
    case 'UPDATED':
      return 'Change';
  }
};

type StringFromActivity = (activity: PolicyActivityBase) => string;
const ActivityTitleMap = mapWithDefault<KnownActivityType, StringFromActivity>({
  defaultValue: activity => snakeToTitleCase(activity.type),
  map: {
    POLICY_UPDATED: policyUpdatedActivityTitle as StringFromActivity,
  },
});

type PolicyActivityComponent = FC<{ activity: PolicyActivityBase }>;
const ActivityIconMap = mapWithDefault<
  KnownActivityType,
  PolicyActivityComponent
>({
  defaultValue: GenericPolicyActivityIcon,
  map: {
    POLICY_UPDATED: PolicyUpdatedActivityIcon as PolicyActivityComponent,
  },
});

export const PolicyActivityExpansionHeader: FC<{
  activity: PolicyActivity;
  'data-testid': string;
}> = ({ activity, 'data-testid': dataTestId }) => {
  const { type, effectiveDate } = activity;
  const getTitle = ActivityTitleMap.get(type);
  const PolicyActivityIcon = ActivityIconMap.get(type);

  return (
    <div className={styles.header} data-testid={dataTestId}>
      <div className={styles.icon} data-testid={`${dataTestId}-icon`}>
        <div className={classNames(styles.assetCircle, styles[type])}>
          <PolicyActivityIcon activity={activity} />
        </div>
      </div>
      <PolicyActivityTitle
        data-testid={`${dataTestId}-title`}
        title={getTitle(activity)}
        date={effectiveDate}
      />
    </div>
  );
};
